import { createFileRoute, ErrorComponent } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { fetchServices, fetchPackages, fetchSettings } from "@/lib/queries";
import { trackViewItemList } from "@/lib/analytics";

const servicesQueryOptions = queryOptions({ queryKey: ["services"], queryFn: fetchServices });
const packagesQueryOptions = queryOptions({ queryKey: ["packages"], queryFn: fetchPackages });
const settingsQueryOptions = queryOptions({ queryKey: ["settings"], queryFn: fetchSettings });
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { money } from "@/lib/format";
import { Plus, Search, Sparkles, MessageCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useCart, isIncluded } from "@/lib/cart";
import { Disclaimer } from "@/components/site/Disclaimer";
import { CONTACT } from "@/lib/contact";
import { pageMeta, jsonLdScriptTag, SITE_BASE_URL } from "@/lib/seo";

export const Route = createFileRoute("/servicios")({
  head: () =>
    pageMeta({
      path: "/servicios",
      title: "Servicios LLC, EIN, USDOT, MC | Truck Company Center",
      description:
        "Pide servicios por separado para tu trucking company: LLC en Florida, EIN, USDOT, MC, BOL, W-9, Central Dispatch, Super Dispatch, logo y más.",
      ogTitle: "Servicios — Truck Company Center",
      ogDescription:
        "Servicios administrativos para car carriers y owner-operators. Precios claros, sin promesas vacías.",
    }),
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(servicesQueryOptions),
      queryClient.ensureQueryData(packagesQueryOptions),
      queryClient.ensureQueryData(settingsQueryOptions),
    ]);
  },
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  component: ServiciosPage,
});

const CATEGORY_ORDER = [
  "Marca", "Constitución", "Documentos", "Requisitos",
  "Plataformas", "Capacitación", "Soporte", "Operación",
];

function ServiciosPage() {
  const { data: services = [] } = useQuery(servicesQueryOptions);
  const { data: packages = [] } = useQuery(packagesQueryOptions);
  const { data: settings } = useQuery(settingsQueryOptions);
  const waBase = settings?.contact?.whatsapp_url ?? CONTACT.whatsappUrl;
  const cart = useCart();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string | null>(null);

  useEffect(() => {
    if (services.length === 0) return;
    trackViewItemList(
      "servicios",
      services.map((s) => ({
        id: s.id,
        slug: s.slug,
        name: s.name,
        price: Number(s.price),
        type: "service" as const,
      }))
    );
  }, [services]);

  const categories = useMemo(() => {
    const present = new Set(services.map((s) => s.category));
    return CATEGORY_ORDER.filter((c) => present.has(c));
  }, [services]);

  const filtered = useMemo(
    () => services
      .filter((s) => (!cat || s.category === cat) && (!q || s.name.toLowerCase().includes(q.toLowerCase())))
      .sort((a, b) => a.sort_order - b.sort_order),
    [services, q, cat]
  );

  const bestPackageFor = (serviceId: string) =>
    packages.find((p) => p.service_ids.includes(serviceId));

  const hasName = cart.items.some((i) => i.type === "service" && i.slug === "llc-florida")
    || cart.items.some((i) => i.type === "package");
  const hasLogo = cart.items.some((i) => i.type === "service" && i.slug === "logo")
    || cart.items.some((i) => i.type === "package");

  const jsonLdServices = useMemo(() => {
    const items = services
      .filter((s) => s.slug !== "cotizacion-seguro")
      .sort((a, b) => a.sort_order - b.sort_order);
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Servicios — Truck Company Center",
      itemListElement: items.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.name,
          description: s.description ?? undefined,
          category: s.category,
          provider: {
            "@type": "Organization",
            name: "Truck Company Center",
            url: SITE_BASE_URL,
          },
        },
      })),
    };
  }, [services]);

  return (
    <div>
      {services.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdScriptTag(jsonLdServices) }}
        />
      )}
      <section className="border-b border-hairline bg-radial-fade">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Servicios</div>
          <h1 className="mt-3 font-display text-4xl font-semibold text-foreground md:text-5xl">Elige los servicios que necesitas</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Pide solo lo que necesitas. Si tu carrito se acerca a un paquete, te avisamos cuánto ahorrarías.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar servicio..." aria-label="Buscar servicio" className="pl-9 bg-surface border-hairline" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCat(null)}
              className={`rounded-full border px-3 py-1.5 text-xs ${!cat ? "border-primary bg-primary/15 text-primary" : "border-hairline bg-surface text-muted-foreground hover:text-foreground"}`}
            >Todos</button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`rounded-full border px-3 py-1.5 text-xs ${cat === c ? "border-primary bg-primary/15 text-primary" : "border-hairline bg-surface text-muted-foreground hover:text-foreground"}`}
              >{c}</button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => {
            const included = isIncluded(s.id, cart.items);
            const inCart = cart.items.some((it) => it.type === "service" && it.id === s.id);
            const pkg = bestPackageFor(s.id);
            const isVariable = s.price_type === "variable";
            return (
              <div key={s.id} className="flex flex-col rounded-md border border-hairline bg-surface p-5">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wider text-label-accent">{s.category}</div>
                  {included && <span className="rounded bg-success/15 px-2 py-0.5 text-[10px] font-medium text-[oklch(0.78_0.16_155)]">Incluido en tu paquete</span>}
                </div>
                <div className="mt-2 font-medium text-foreground">{s.name}</div>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.description}</p>
                {s.note && <p className="mt-2 text-[11px] italic text-muted-foreground">{s.note}</p>}
                {pkg && !included && (
                  <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-label-accent">
                    <Sparkles className="h-3 w-3" /> Mejor valor en el paquete {pkg.name}
                  </p>
                )}
                <div className="mt-4 flex items-center justify-between">
                  {isVariable ? (
                    <span className="font-display text-base font-semibold text-foreground">Precio variable</span>
                  ) : (
                    <span className="font-display text-lg font-semibold text-foreground">{money(s.price)}</span>
                  )}
                  {included ? (
                    <Button size="sm" variant="outline" disabled className="border-hairline opacity-60">Incluido</Button>
                  ) : isVariable ? (
                    <a
                      href={`${waBase.split("?")[0]}?text=${encodeURIComponent(`Hola, quiero una cotización para: ${s.name}`)}`}
                      target="_blank" rel="noreferrer"
                    >
                      <Button size="sm" variant="outline" className="border-hairline">
                        <MessageCircle className="mr-1 h-3 w-3" /> Solicitar cotización
                      </Button>
                    </a>
                  ) : (
                    <Button size="sm" disabled={inCart} className="bg-primary text-primary-foreground hover:bg-primary/90"
                      onClick={() => cart.addService({ id: s.id, slug: s.slug, name: s.name, price: Number(s.price) })}>
                      {inCart ? "Añadido ✓" : <><Plus className="mr-1 h-3 w-3" />Añadir</>}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="mt-10 rounded-md border border-hairline bg-surface p-8 text-center text-muted-foreground">
            Ningún servicio coincide con tu búsqueda.
          </div>
        )}

        <div className="mt-10">
          <Disclaimer />
        </div>
      </section>
    </div>
  );
}
